import type { OcrResult, OcrRuntimeParamsInput, PaddleOCR as PaddleOcrClass } from '@paddleocr/paddleocr-js'
import type { GamePreset } from '@/presets/types'
import { resolveCachedOcrModels } from '@/services/ocr-resource-cache'

export interface OcrReading {
  readonly text: string
  /** Full accepted OCR output when `text` has been narrowed to one metric candidate. */
  readonly fullText?: string
  readonly items: readonly OcrTextItem[]
  readonly confidence: number
  readonly durationMs: number
  readonly detectedItems: number
  readonly acceptedItems: number
  readonly bestConfidence: number
}

export interface OcrTextItem {
  readonly text: string
  readonly confidence: number
  readonly polygon: readonly (readonly [number, number])[]
}

type OcrEngineInstance = Awaited<ReturnType<typeof PaddleOcrClass.create>>
const DEFAULT_PREDICTION_TIMEOUT_MS = 12_000

interface InitializingEngine {
  readonly generation: number
  readonly promise: Promise<OcrEngineInstance>
}

class OcrEngineResetError extends Error {
  constructor() {
    super('OCR engine was reset')
    this.name = 'OcrEngineResetError'
  }
}

export class OcrPredictionTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`OCR prediction timed out after ${timeoutMs}ms`)
    this.name = 'OcrPredictionTimeoutError'
  }
}

export class OcrEngine {
  private engine: OcrEngineInstance | null = null
  private initializing: InitializingEngine | null = null
  private pendingDisposal: Promise<void> | null = null
  private busyToken: symbol | null = null
  private generation = 0

  constructor(
    private readonly models: GamePreset['remoteResources']['ocrModels'],
    private readonly predictionTimeoutMs = DEFAULT_PREDICTION_TIMEOUT_MS,
  ) {}

  async warmup(): Promise<void> {
    await this.ensureEngine()
  }

  async recognize(image: ImageData, options: Readonly<OcrRuntimeParamsInput> = {}): Promise<OcrReading | null> {
    if (this.busyToken) return null
    const token = Symbol('ocr-prediction')
    const generation = this.generation
    this.busyToken = token
    try {
      const engine = await this.ensureEngine()
      if (generation !== this.generation) return null
      const startedAt = performance.now()
      const prediction = engine.predict(image, {
        textDetLimitSideLen: Math.max(image.width, image.height),
        textRecScoreThresh: 0.35,
        ...options,
      })
      const [result] = await this.withPredictionTimeout(prediction, engine, generation)
      if (generation !== this.generation) return null
      const scoreThreshold = options.textRecScoreThresh ?? options.text_rec_score_thresh ?? 0.35
      return result ? this.toReading(result, performance.now() - startedAt, scoreThreshold) : null
    } finally {
      if (this.busyToken === token) this.busyToken = null
    }
  }

  async dispose(): Promise<void> {
    await this.resetEngine()
  }

  async recover(): Promise<void> {
    await this.resetEngine()
  }

  private async resetEngine(): Promise<void> {
    const instance = this.engine
    const initialization = this.initializing?.promise
    this.generation += 1
    this.engine = null
    this.initializing = null
    this.busyToken = null
    if (instance) {
      await this.queueDisposal(instance)
      return
    }
    if (initialization) {
      try {
        await initialization
      } catch {
        // A reset initialization disposes its late worker before rejecting.
      }
      return
    }
    await this.pendingDisposal
  }

  private async ensureEngine(): Promise<OcrEngineInstance> {
    await this.pendingDisposal
    if (this.engine) return this.engine
    const generation = this.generation
    if (!this.initializing || this.initializing.generation !== generation) {
      let promise!: Promise<OcrEngineInstance>
      promise = Promise.all([
        import('@paddleocr/paddleocr-js'),
        resolveCachedOcrModels(this.models),
      ])
        .then(async ([{ PaddleOCR }, cached]) => {
          try {
            if (generation !== this.generation) throw new OcrEngineResetError()
            return await PaddleOCR.create({
              worker: true,
              initialize: true,
              lang: 'ch',
              textDetectionModelName: cached.models.detection.name,
              textDetectionModelAsset: { url: cached.models.detection.url },
              textRecognitionModelName: cached.models.recognition.name,
              textRecognitionModelAsset: { url: cached.models.recognition.url },
              textDetectionBatchSize: 1,
              textRecognitionBatchSize: 1,
              ortOptions: {
                backend: 'wasm',
                wasmPaths: 'https://registry.npmmirror.com/onnxruntime-web/1.29.0/files/dist/',
                numThreads: 1,
                simd: true,
                proxy: false,
              },
            })
          } finally {
            cached.release()
          }
        })
        .then(async (engine) => {
          if (generation !== this.generation) {
            await this.safeDispose(engine)
            throw new OcrEngineResetError()
          }
          this.engine = engine
          return engine
        })
        .catch((error: unknown) => {
          if (this.initializing?.promise === promise) this.initializing = null
          throw error
        })
      this.initializing = { generation, promise }
    }
    return this.initializing.promise
  }

  private async withPredictionTimeout(
    prediction: ReturnType<OcrEngineInstance['predict']>,
    engine: OcrEngineInstance,
    generation: number,
  ): Promise<Awaited<ReturnType<OcrEngineInstance['predict']>>> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new OcrPredictionTimeoutError(this.predictionTimeoutMs)), this.predictionTimeoutMs)
    })
    try {
      return await Promise.race([prediction, timeout])
    } catch (error) {
      if (error instanceof OcrPredictionTimeoutError) this.invalidateEngine(engine, generation)
      throw error
    } finally {
      if (timeoutId !== undefined) clearTimeout(timeoutId)
    }
  }

  private invalidateEngine(instance: OcrEngineInstance, generation: number): void {
    if (this.engine !== instance || this.generation !== generation) return
    this.generation += 1
    this.engine = null
    this.initializing = null
    this.busyToken = null
    void this.queueDisposal(instance)
  }

  private queueDisposal(instance: OcrEngineInstance): Promise<void> {
    const previous = this.pendingDisposal
    let disposal!: Promise<void>
    disposal = (async () => {
      await previous
      await this.safeDispose(instance)
    })().finally(() => {
      if (this.pendingDisposal === disposal) this.pendingDisposal = null
    })
    this.pendingDisposal = disposal
    return disposal
  }

  private async safeDispose(instance: OcrEngineInstance): Promise<void> {
    try {
      await instance.dispose()
    } catch {
      // A failed worker teardown must not prevent a fresh engine from starting.
    }
  }

  private toReading(result: OcrResult, fallbackDuration: number, scoreThreshold: number): OcrReading {
    const accepted = result.items.filter((item) => item.score >= scoreThreshold)
    const confidence = accepted.length
      ? accepted.reduce((sum, item) => sum + item.score, 0) / accepted.length
      : 0
    return {
      text: accepted.map((item) => item.text).join(' '),
      items: accepted.map((item) => ({
        text: item.text,
        confidence: item.score,
        polygon: item.poly,
      })),
      confidence,
      durationMs: result.metrics.totalMs || fallbackDuration,
      detectedItems: result.items.length,
      acceptedItems: accepted.length,
      bestConfidence: result.items.reduce((best, item) => Math.max(best, item.score), 0),
    }
  }
}

const sharedEngines = new Map<string, OcrEngine>()

export function getSharedOcrEngine(models: GamePreset['remoteResources']['ocrModels']): OcrEngine {
  const key = sharedOcrEngineKey(models)
  const existing = sharedEngines.get(key)
  if (existing) return existing
  const engine = new OcrEngine(models)
  sharedEngines.set(key, engine)
  return engine
}

export async function releaseSharedOcrEngine(models: GamePreset['remoteResources']['ocrModels']): Promise<void> {
  const key = sharedOcrEngineKey(models)
  const engine = sharedEngines.get(key)
  if (!engine) return
  sharedEngines.delete(key)
  await engine.dispose()
}

function sharedOcrEngineKey(models: GamePreset['remoteResources']['ocrModels']): string {
  return `${models.detection.url}@${models.detection.version}|${models.recognition.url}@${models.recognition.version}`
}
